import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import api, { formatApiError } from "@/lib/api";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger, DialogDescription } from "@/components/ui/dialog";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Leaf, LogOut, ShieldCheck, UserPlus, Users, Trash2, Eye, Activity, BellRing, Gift, Crown, TrendingUp, CreditCard, Mail, MailOpen, Tag, MousePointerClick, KeyRound } from "lucide-react";
import { toast } from "sonner";
import { DiscountCodesAdmin } from "@/components/admin/DiscountCodesAdmin";
import { PersonalCodesAdmin } from "@/components/admin/PersonalCodesAdmin";
import { SubscriptionChart } from "@/components/admin/SubscriptionChart";
import { UsagePanel } from "@/components/admin/UsagePanel";
import { EmailScheduleAdmin } from "@/components/admin/EmailScheduleAdmin";
import { AppSubscribers, AppStoreBadge } from "@/components/admin/AppSubscribers";
import { DiscountLeaderboard } from "@/components/admin/DiscountLeaderboard";
import { UserBillingCard, DiscountBadge } from "@/components/admin/UserBilling";

export default function AdminDashboard() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [users, setUsers] = useState([]);
  const [form, setForm] = useState({ name: "", email: "", password: "" });
  const [creating, setCreating] = useState(false);
  const [open, setOpen] = useState(false);
  const [detail, setDetail] = useState(null);
  const [reminding, setReminding] = useState(false);
  const [stats, setStats] = useState(null);
  const [engStats, setEngStats] = useState(null);
  const [previewing, setPreviewing] = useState(false);
  const [reporting, setReporting] = useState(false);

  const sendReset = async (u) => {
    if (!window.confirm(`Inviare a ${u.email} un link per reimpostare la password?`)) return;
    try {
      const { data } = await api.post(`/admin/users/${u.user_id}/send-reset`, { origin_url: window.location.origin });
      toast.success(`Link di reset inviato a ${data.sent_to}`);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    }
  };

  const sendReport = async () => {
    setReporting(true);
    try {
      const { data } = await api.post("/admin/weekly-report");
      toast.success(`Report settimanale inviato a ${data.sent_to}`);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally { setReporting(false); }
  };

  const load = async () => {
    setLoading(true);
    try {
      const [uRes, sRes, eRes] = await Promise.all([
        api.get("/admin/users"), api.get("/admin/stats"), api.get("/admin/engagement-stats"),
      ]);
      setUsers(uRes.data);
      setStats(sRes.data);
      setEngStats(eRes.data);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const sendPreview = async () => {
    setPreviewing(true);
    try {
      const { data } = await api.post("/admin/engagement-preview");
      toast.success(`Anteprima inviata a ${data.sent_to}`);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally { setPreviewing(false); }
  };

  const create = async () => {
    if (!form.name || !form.email || form.password.length < 6) {
      toast.error("Compila tutti i campi (password min 6 caratteri)"); return;
    }
    setCreating(true);
    try {
      await api.post("/admin/users", form);
      toast.success("Utente creato");
      setForm({ name: "", email: "", password: "" });
      setOpen(false);
      load();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally { setCreating(false); }
  };

  const remove = async (id) => {
    if (!window.confirm("Eliminare questo utente e tutti i suoi dati?")) return;
    try {
      await api.delete(`/admin/users/${id}`);
      toast.success("Utente eliminato");
      load();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };

  const viewDetail = async (id) => {
    try {
      const { data } = await api.get(`/admin/users/${id}`);
      setDetail(data);
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };

  const toggleAccess = async (u) => {
    try {
      await api.put(`/admin/users/${u.user_id}/access`, { comp: !u.comp });
      toast.success(!u.comp ? "Accesso Premium regalato" : "Accesso Premium revocato");
      load();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };

  const doLogout = async () => { await logout(); navigate("/"); };

  const sendReminders = async () => {
    setReminding(true);
    try {
      const { data } = await api.post("/admin/send-weight-reminders");
      toast.success(data.sent > 0 ? `Promemoria inviati: ${data.sent}` : "Nessun promemoria da inviare (tutti aggiornati)");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally { setReminding(false); }
  };

  return (
    <div className="min-h-screen bg-background">
      <header className="sticky top-0 z-50 backdrop-blur-md bg-background/80 border-b">
        <div className="max-w-6xl mx-auto px-4 h-16 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="h-9 w-9 rounded-xl bg-primary text-primary-foreground grid place-items-center"><Leaf className="h-5 w-5" /></div>
            <span className="font-heading text-lg font-semibold">DietFlix</span>
            <Badge variant="secondary" className="gap-1"><ShieldCheck className="h-3 w-3" /> Admin</Badge>
          </div>
          <div className="flex items-center gap-3">
            <span className="text-sm text-muted-foreground hidden sm:inline">{user?.email}</span>
            <Button variant="outline" size="sm" onClick={doLogout} data-testid="admin-logout-button"><LogOut className="h-4 w-4 mr-2" /> Esci</Button>
          </div>
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-4 py-8 space-y-6">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div>
            <h1 className="font-heading text-3xl font-semibold">Gestione utenti</h1>
            <p className="text-muted-foreground">Crea e monitora i profili dei tuoi assistiti.</p>
          </div>
          <div className="flex flex-wrap gap-2">
            <Button variant="outline" onClick={sendPreview} disabled={previewing} data-testid="send-preview-button">
              <Mail className="h-4 w-4 mr-2" /> {previewing ? "Invio…" : "Anteprima email"}
            </Button>
            <Button variant="outline" onClick={sendReport} disabled={reporting} data-testid="send-weekly-report-button">
              <TrendingUp className="h-4 w-4 mr-2" /> {reporting ? "Invio…" : "Report settimanale"}
            </Button>
            <Button variant="outline" onClick={sendReminders} disabled={reminding} data-testid="send-reminders-button">
              <BellRing className="h-4 w-4 mr-2" /> {reminding ? "Invio…" : "Promemoria peso"}
            </Button>
            <Dialog open={open} onOpenChange={setOpen}>
              <DialogTrigger asChild>
                <Button data-testid="open-create-user-button"><UserPlus className="h-4 w-4 mr-2" /> Nuovo utente</Button>
              </DialogTrigger>
              <DialogContent data-testid="create-user-dialog">
                <DialogHeader><DialogTitle className="font-heading">Crea nuovo utente</DialogTitle>
                  <DialogDescription>L'utente riceverà un'email con le credenziali di accesso.</DialogDescription>
                </DialogHeader>
                <div className="space-y-4">
                  <div className="space-y-2"><Label>Nome</Label><Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="new-user-name" /></div>
                  <div className="space-y-2"><Label>Email</Label><Input type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} data-testid="new-user-email" /></div>
                  <div className="space-y-2"><Label>Password provvisoria</Label><Input value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} data-testid="new-user-password" /></div>
                </div>
                <p className="text-xs text-muted-foreground">All'utente verrà inviata un'email con le credenziali di accesso.</p>
                <DialogFooter>
                  <Button onClick={create} disabled={creating} data-testid="submit-create-user-button">{creating ? "Creazione…" : "Crea utente"}</Button>
                </DialogFooter>
              </DialogContent>
            </Dialog>
          </div>
        </div>

        {stats && (
          <div className="grid grid-cols-2 md:grid-cols-5 gap-3" data-testid="admin-stats">
            {[
              { key: "paying", label: "Paganti", value: stats.paying, icon: CreditCard, cls: "text-primary" },
              { key: "included", label: "Inclusi", value: stats.included, icon: Gift, cls: "text-accent" },
              { key: "expired", label: "Scaduti", value: stats.expired, icon: Activity, cls: "text-destructive" },
              { key: "free", label: "Free", value: stats.free, icon: Users, cls: "text-muted-foreground" },
              { key: "mrr", label: "Incassi stimati/mese", value: `€${stats.estimated_mrr.toFixed(2).replace(".", ",")}`, icon: TrendingUp, cls: "text-primary" },
            ].map((s) => (
              <Card key={s.key} className="rounded-2xl" data-testid={`stat-${s.key}`}>
                <CardContent className="p-4">
                  <div className="flex items-center gap-2 text-xs text-muted-foreground"><s.icon className={`h-4 w-4 ${s.cls}`} /> {s.label}</div>
                  <div className="font-mono-data text-2xl font-semibold mt-1">{s.value}</div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}

        {engStats && (
          <Card className="rounded-2xl" data-testid="engagement-stats">
            <CardHeader><CardTitle className="font-heading text-lg flex items-center gap-2"><Mail className="h-5 w-5 text-primary" /> Email settimanali di engagement</CardTitle></CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-3">
                {[
                  { key: "upsell", label: "Upsell (non premium)", d: engStats.upsell },
                  { key: "personal", label: "Codici personali", d: engStats.personal },
                  { key: "feedback", label: "Feedback (premium)", d: engStats.feedback },
                  { key: "total", label: "Totale", d: engStats.total },
                ].map((s) => (
                  <div key={s.key} className="rounded-xl bg-secondary/50 p-4" data-testid={`eng-stat-${s.key}`}>
                    <div className="text-xs text-muted-foreground mb-1">{s.label}</div>
                    <div className="flex items-center gap-4">
                      <div>
                        <div className="text-xs text-muted-foreground flex items-center gap-1"><Mail className="h-3 w-3" /> Inviate</div>
                        <div className="font-mono-data text-xl font-semibold">{s.d.sent}</div>
                      </div>
                      <div>
                        <div className="text-xs text-muted-foreground flex items-center gap-1"><MailOpen className="h-3 w-3" /> Aperte</div>
                        <div className="font-mono-data text-xl font-semibold">{s.d.opened}</div>
                      </div>
                      <div>
                        <div className="text-xs text-muted-foreground">Tasso</div>
                        <div className="font-mono-data text-xl font-semibold text-primary">{s.d.open_rate}%</div>
                      </div>
                    </div>
                    {s.key !== "feedback" && (
                      <div className="flex items-center gap-4 mt-3 pt-3 border-t border-border/60">
                        <div>
                          <div className="text-xs text-muted-foreground flex items-center gap-1"><MousePointerClick className="h-3 w-3" /> Click</div>
                          <div className="font-mono-data text-lg font-semibold" data-testid={`eng-clicks-${s.key}`}>{s.d.clicked}</div>
                        </div>
                        <div>
                          <div className="text-xs text-muted-foreground">Tasso click</div>
                          <div className="font-mono-data text-lg font-semibold text-primary" data-testid={`eng-click-rate-${s.key}`}>{s.d.click_rate}%</div>
                        </div>
                        <div>
                          <div className="text-xs text-muted-foreground flex items-center gap-1"><Crown className="h-3 w-3" /> Abbonati</div>
                          <div className="font-mono-data text-lg font-semibold" data-testid={`eng-converted-${s.key}`}>{s.d.converted}</div>
                        </div>
                      </div>
                    )}
                  </div>
                ))}
              </div>
              <p className="text-xs text-muted-foreground mt-3">Click = persone che hanno cliccato "Passa a Premium"; Abbonati = abbonamenti attivati entro 30 giorni dal click. Il tasso di apertura è stimato (alcuni client email bloccano le immagini di tracciamento).</p>
            </CardContent>
          </Card>
        )}

        <UsagePanel />

        <SubscriptionChart />

        <AppSubscribers onChanged={load} />

        <DiscountCodesAdmin />

        <DiscountLeaderboard />

        <PersonalCodesAdmin />

        <EmailScheduleAdmin />

        <Card className="rounded-2xl">
          <CardHeader><CardTitle className="font-heading text-lg flex items-center gap-2"><Users className="h-5 w-5 text-primary" /> Utenti ({users.length})</CardTitle></CardHeader>
          <CardContent>
            {loading ? <Skeleton className="h-40 rounded-xl" /> : users.length === 0 ? (
              <p className="text-muted-foreground py-8 text-center">Nessun utente. Creane uno per iniziare.</p>
            ) : (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Nome</TableHead><TableHead>Email</TableHead>
                    <TableHead>Obiettivo</TableHead><TableHead>Peso</TableHead>
                    <TableHead>Stato</TableHead><TableHead>Accesso</TableHead><TableHead>Codice sconto</TableHead><TableHead className="text-right">Azioni</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {users.map((u) => (
                    <TableRow key={u.user_id} data-testid={`user-row-${u.email}`}>
                      <TableCell className="font-medium">
                        <span className="flex items-center gap-2 flex-wrap">{u.name}
                          <AppStoreBadge u={u} />
                        </span>
                      </TableCell>
                      <TableCell className="text-muted-foreground">{u.email}</TableCell>
                      <TableCell>{u.targets?.goal_label || "—"}</TableCell>
                      <TableCell className="font-mono-data">{u.latest_weight ? `${u.latest_weight} kg` : "—"}</TableCell>
                      <TableCell>
                        {u.onboarding_completed
                          ? <Badge className="bg-primary/15 text-primary border-primary/30" variant="outline">Attivo</Badge>
                          : <Badge variant="outline">In attesa</Badge>}
                      </TableCell>
                      <TableCell>
                        {(() => { const a = accessInfo(u); return <Badge variant="outline" className={a.cls} data-testid={`access-badge-${u.email}`}>{a.label}</Badge>; })()}
                      </TableCell>
                      <TableCell><DiscountBadge d={u.discount} testId={`user-discount-${u.email}`} /></TableCell>
                      <TableCell className="text-right whitespace-nowrap">
                        <Button variant="ghost" size="icon" title={u.comp ? "Revoca accesso" : "Regala accesso"} onClick={() => toggleAccess(u)} data-testid={`toggle-access-${u.email}`}>
                          <Gift className={`h-4 w-4 ${u.comp ? "text-accent" : ""}`} />
                        </Button>
                        <Button variant="ghost" size="icon" onClick={() => viewDetail(u.user_id)} data-testid={`view-user-${u.email}`}><Eye className="h-4 w-4" /></Button>
                        <Button variant="ghost" size="icon" title="Invia link di reset password" onClick={() => sendReset(u)} data-testid={`reset-user-${u.email}`}><KeyRound className="h-4 w-4" /></Button>
                        <Button variant="ghost" size="icon" onClick={() => remove(u.user_id)} data-testid={`delete-user-${u.email}`}><Trash2 className="h-4 w-4 text-destructive" /></Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </CardContent>
        </Card>
      </main>

      <Dialog open={!!detail} onOpenChange={(o) => !o && setDetail(null)}>
        <DialogContent className="max-w-lg" data-testid="user-detail-dialog">
          <DialogHeader><DialogTitle className="font-heading flex items-center gap-2"><Activity className="h-5 w-5" /> {detail?.name}</DialogTitle>
            <DialogDescription>Dettaglio profilo, progressi e gestione accesso Premium.</DialogDescription>
          </DialogHeader>
          {detail && (
            <div className="space-y-4 text-sm max-h-[70vh] overflow-y-auto">
              <div className="grid grid-cols-2 gap-3">
                <Info label="Email" value={detail.email} />
                <Info label="Obiettivo" value={detail.targets?.goal_label || "—"} />
                <Info label="Fabbisogno" value={detail.targets?.target_kcal ? `${detail.targets.target_kcal} kcal` : "—"} />
                <Info label="Peso attuale" value={detail.profile?.weight_kg ? `${detail.profile.weight_kg} kg` : "—"} />
                <Info label="Patologie" value={detail.profile?.pathologies || "nessuna"} />
                <Info label="Farmaci" value={detail.profile?.medications || "nessuno"} />
              </div>
              <UserBillingCard billing={detail.billing} discount={detail.discount} />
              <div className="flex items-center justify-between rounded-xl bg-secondary/50 p-3">
                <div>
                  <div className="text-xs text-muted-foreground">Accesso Premium</div>
                  <div className="font-medium">{accessInfo(detail).label}</div>
                </div>
                <Button size="sm" variant="outline" onClick={() => { toggleAccess(detail); setDetail(null); }} data-testid="detail-toggle-access">
                  <Gift className="h-4 w-4 mr-2" /> {detail.comp ? "Revoca" : "Regala accesso"}
                </Button>
              </div>
              <div>
                <div className="font-medium mb-1">Storico peso ({detail.weights?.length || 0})</div>
                <div className="flex flex-wrap gap-2">
                  {(detail.weights || []).slice(-8).map((w, i) => (
                    <span key={i} className="rounded-md bg-secondary px-2 py-0.5 font-mono-data text-xs">{w.date?.slice(5)}: {w.weight_kg}kg</span>
                  ))}
                  {(!detail.weights || detail.weights.length === 0) && <span className="text-muted-foreground">—</span>}
                </div>
              </div>
              <div>
                <div className="font-medium mb-1">Analisi ({detail.blood_tests?.length || 0})</div>
                {(detail.blood_tests || []).length === 0 && <span className="text-muted-foreground">Nessuna analisi</span>}
                {(detail.blood_tests || []).slice(0, 3).map((b, i) => (
                  <p key={i} className="text-muted-foreground text-xs">{b.date}: {b.interpretation?.overall?.slice(0, 120) || "—"}</p>
                ))}
              </div>
              <div>
                <div className="font-medium mb-1">Dieta</div>
                <span className="text-muted-foreground">{detail.diet?.plan?.days?.length ? `Piano di ${detail.diet.plan.days.length} giorni generato` : "Nessuna dieta generata"}</span>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}

function accessInfo(u) {
  if (u.comp) return { label: "Incluso", cls: "bg-accent/15 text-accent border-accent/30" };
  if (u.subscription_status === "active") return { label: "Pagante", cls: "bg-primary/15 text-primary border-primary/30" };
  if (u.plan) return { label: "Scaduto", cls: "bg-destructive/15 text-destructive border-destructive/30" };
  return { label: "Free", cls: "" };
}

function Info({ label, value }) {
  return (
    <div className="rounded-xl bg-secondary/50 p-3">
      <div className="text-xs text-muted-foreground">{label}</div>
      <div className="font-medium break-words">{value}</div>
    </div>
  );
}
